/**
 * App root: providers + router.
 *
 * Provider order (outer → inner): QueryClientProvider → ThemeProvider →
 * AuthProvider → ToastProvider → BrowserRouter. Pages can use TanStack Query,
 * theme, auth and toasts anywhere in the tree.
 *
 * Routes (React Router v6):
 *   /login                     public            -> Login
 *   /            (ProtectedLayout)
 *     /                        index             -> Overview
 *     /content                                   -> Content
 *     /content/new                               -> ContentEditor
 *     /content/:id                               -> ContentEditor
 *     /media                                     -> Media
 *     /users                                     -> Users
 *     /affirmations                              -> Affirmations
 *     /marketing                                 -> Marketing (Promos CRUD)
 *     /referrals                                 -> Referrals
 *     /finance                                   -> Finance
 *     /subscriptions                             -> Subscriptions
 *     /announcements                             -> Announcements
 *     /email-templates                           -> EmailTemplates
 *     /notification-templates                    -> NotificationTemplates
 *     /app-copy                                  -> AppCopy
 *     /app-content                               -> AppContent
 *     /coach-rules                               -> CoachRules
 *     /plan-config                               -> PlanConfig
 *     /gamification                              -> Gamification
 *     /programs                                  -> Programs
 *     /challenges                  (Super)       -> Challenges
 *     /advanced                    (Super)       -> AdvancedConfig
 *     /audit                       (Super)       -> AuditLog
 *     /settings                    (Super)       -> Settings
 *   *                          catch-all         -> redirect to /
 *
 * Protected pages are lazy()-loaded. This file is part of the FOUNDATION —
 * Pages engineers must not edit it (only replace the page files it points to).
 */
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './lib/auth';
import { ThemeProvider } from './lib/theme';
import { ToastProvider } from './components/ui/Toast';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui/Spinner';
import Login from './pages/Login';

const Overview = lazy(() => import('./pages/Overview'));
const Content = lazy(() => import('./pages/Content'));
const ContentEditor = lazy(() => import('./pages/ContentEditor'));
const CueReview = lazy(() => import('./pages/CueReview'));
const Media = lazy(() => import('./pages/Media'));
const Users = lazy(() => import('./pages/Users'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const Affirmations = lazy(() => import('./pages/Affirmations'));
const Settings = lazy(() => import('./pages/Settings'));
const AiVoice = lazy(() => import('./pages/AiVoice'));
// Comms & Commerce (AdminCommerceModule).
const Marketing = lazy(() => import('./pages/Marketing'));
const Referrals = lazy(() => import('./pages/Referrals'));
const Finance = lazy(() => import('./pages/Finance'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const Announcements = lazy(() => import('./pages/Announcements'));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'));
// Config & Copy (AdminCopyModule).
const NotificationTemplates = lazy(() => import('./pages/NotificationTemplates'));
const AppCopy = lazy(() => import('./pages/AppCopy'));
const AppContent = lazy(() => import('./pages/AppContent'));
const CoachRules = lazy(() => import('./pages/CoachRules'));
const PlanConfig = lazy(() => import('./pages/PlanConfig'));
// Retention (AdminRetentionModule).
const Programs = lazy(() => import('./pages/Programs'));
const Challenges = lazy(() => import('./pages/Challenges'));
// Advanced + Gamification (AdminAdvancedModule).
const Gamification = lazy(() => import('./pages/Gamification'));
const AdvancedConfig = lazy(() => import('./pages/AdvancedConfig'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function PageFallback() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route path="/login" element={<Login />} />

                  <Route element={<Layout />}>
                    <Route index element={<Overview />} />
                    <Route path="content" element={<Content />} />
                    <Route path="content/new" element={<ContentEditor />} />
                    <Route path="content/:id" element={<ContentEditor />} />
                    <Route path="cue-review" element={<CueReview />} />
                    <Route path="media" element={<Media />} />
                    <Route path="users" element={<Users />} />
                    <Route path="affirmations" element={<Affirmations />} />

                    {/* Comms & Commerce (AdminCommerceModule). */}
                    <Route path="marketing" element={<Marketing />} />
                    <Route path="referrals" element={<Referrals />} />
                    <Route path="finance" element={<Finance />} />
                    <Route path="subscriptions" element={<Subscriptions />} />
                    <Route path="announcements" element={<Announcements />} />
                    <Route path="email-templates" element={<EmailTemplates />} />

                    {/* Config & Copy (AdminCopyModule). */}
                    <Route path="notification-templates" element={<NotificationTemplates />} />
                    <Route path="app-copy" element={<AppCopy />} />
                    <Route path="app-content" element={<AppContent />} />
                    <Route path="coach-rules" element={<CoachRules />} />
                    <Route path="plan-config" element={<PlanConfig />} />

                    {/* Retention (AdminRetentionModule). */}
                    <Route path="programs" element={<Programs />} />
                    <Route path="challenges" element={<Challenges />} />

                    {/* Advanced + Gamification (AdminAdvancedModule). */}
                    <Route path="gamification" element={<Gamification />} />
                    <Route path="advanced" element={<AdvancedConfig />} />

                    {/* Super-only config. */}
                    <Route path="audit" element={<AuditLog />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="ai" element={<AiVoice />} />
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
